import Util from '../../../forecast-app/shared/util/util';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';

/**
 * Backend refusals of the approval mutation, mapped to the copy that explains them.
 *
 * These refusals do not reach the client as Relay failures, which is why they were invisible
 * before this helper existed. `UpdateApprovalStatusMutation` catches the 400 that
 * `ExceptionBuilder.CustomValidationStringException` produces and returns its message inside the
 * payload's `errors` array (graphql/src/util/ErrorHandler.js), so the mutation completes
 * successfully as far as Relay is concerned and `Util.CommitMutation`'s error-modal flow never
 * runs. The success handler is therefore the only place these can be surfaced.
 *
 * Matching is on the raw English message because that message is all the graph forwards. There is
 * no error code to key on here, unlike the `<CODE>: <correlationId>` refusals that
 * `getValidationErrorMessageId` handles, so this cannot reuse that mapping.
 *
 * These stay off the MUTATION_ERROR path deliberately, for the reason given on
 * VALIDATION_ERROR_MESSAGE_IDS in util.js: they are user-actionable "no"s, not application
 * failures, and reporting them to Sentry would file expected behaviour as an error.
 */
const APPROVAL_ERROR_MESSAGE_IDS: ReadonlyArray<readonly [string, string]> = [
	['Decline reason is required', 'time_approval.error.decline_reason_required'],
	['Feature TIMESHEET_APPROVAL_NEW_STATUSES is required', 'time_approval.error.new_statuses_required'],
	// These two arrive as bare `ErrorCode.name()` constants, not prose: TimeRegLogic throws them via
	// ExceptionBuilder.CustomValidationException(ErrorCode), which sends `errorCode.name()`. Without
	// an entry here the fallback below would render the literal string
	// "TIME_REGISTRATION_IN_LOCKED_PERIOD" at the user. The locked-period one is the most common
	// refusal this screen produces — approving time in a closed period is routine month-end
	// behaviour — so it is the refusal users actually meet.
	//
	// The copy is deliberately new rather than reusing
	// integrations.jira.error_code.TIME_REGISTRATION_IN_LOCKED_PERIOD, whose text ends "cannot be
	// deleted" — the wrong verb for an approval refusal.
	['TIME_REGISTRATION_IN_LOCKED_PERIOD', 'time_approval.error.locked_period'],
	['NOT_USING_TIME_APPROVAL', 'time_approval.error.approval_not_enabled'],
];

/**
 * Shows the refusal carried in an `updateApprovalStatus` payload's `errors` array, if there is one.
 *
 * Call this from every `updateApprovalStatus` success handler: the payload reports refusals here
 * rather than by failing, so a handler that only reads the success fields cannot tell a rejected
 * action from an applied one.
 *
 * @param errors the payload's `errors` array; null/empty means the action was applied.
 * @param closeOtherModals replace the modal that triggered the action instead of stacking on it.
 * @returns true when a refusal was found and shown, so callers can skip their success handling.
 */
export const showApprovalErrorModal = (
	errors: ReadonlyArray<string | null> | null | undefined,
	closeOtherModals = false
): boolean => {
	if (!errors || errors.length === 0) return false;

	// Resolve the message BEFORE consulting Sage. Util.checkForSageErrorAndShowModal reads
	// `errors[0].includes(...)` with no null guard, so handing it a null/non-string element throws
	// — and because that throw happens inside Relay's onCompleted, the user sees nothing at all,
	// which is the exact silent failure this helper exists to remove. Ordering the guard first means this
	// call site can no longer reach that crash.
	//
	// This is defence in depth, not the fix: the missing guard lives in util.js, which this change
	// does not touch and which has ~40 other call sites with the same exposure. That belongs in its
	// own ticket rather than riding in on a timesheet change.
	const message = typeof errors[0] === 'string' ? (errors[0] as string) : '';

	// Sage owns its own copy and keeps the precedence it had before this helper existed.
	if (message && Util.checkForSageErrorAndShowModal(errors, closeOtherModals)) return true;
	const known = APPROVAL_ERROR_MESSAGE_IDS.find(([needle]) => message.includes(needle));
	showModal(
		{
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'common.invalid_action_modal_title',
			// An unrecognised refusal is shown verbatim rather than dropped. It is untranslated
			// English, but the alternative — showing nothing —
			// made a failed reverse approval indistinguishable from a successful one.
			...(known
				? {warningInformationIds: [known[1]]}
				: message
				? {warningInformation: [message]}
				: {warningInformationIds: ['time_approval.error.generic']}),
		},
		closeOtherModals
	);
	return true;
};
